import type { ContractClause, NegotiationView, SharedObjectView } from "@closer/shared";
import {
  answeredText,
  attributionLabel,
  findParentNegotiation,
  NEGOTIATION_STATUS_NAMES,
  negotiationItemStatusName,
} from "@closer/shared";
import { Badge, Button, Card, FormField, Select, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useSharedCurrentVersion } from "./useSharedCurrentVersion";

/**
 * MÜZAKERE — KATILIMCI tarafı (CLOS-873; uçlar CLOS-252 `[145]`).
 *
 * ### ⚠ Neden sahip tarafının bileşeni yeniden kullanılmadı
 *
 * `ContractNegotiationSection` yolu `/api/contracts/${id}/...` diye sabit yazıyor — ama
 * asıl sebep yol değil FİİL KÜMESİ: sahip ORKESTRE ediyor (uygula, gönder, iptal),
 * katılımcı YANITLIYOR (karşı öneri, geri çek, karar). İki fiil kümesini tek bileşene
 * koymak ikisini de bulanıklaştırırdı. Fiil taşımayan parçalar (durum ve karar metinleri)
 * ise ORTAK SÖZLÜKTE — `NEGOTIATION_STATUS_NAMES` iki ekranda tek Türkçe demek.
 *
 * ### ⚠ "METNE UYGULA" BURADA YOK — ve bu bir eksik değil
 *
 * Uygulama kanonik metni değiştiriyor; o yetki sahibinde. Paylaşılan yüzeyde böyle bir uç
 * da yok (`sharedContractRoutes.ts`). Düğmeyi koyup 404 almak, kullanıcıya var olmayan
 * bir yetenek vaat etmek olurdu.
 *
 * ### ⚠ "HEDEF METİN DEĞİŞTİ" BAYRAĞI BU YÜZEYDE ÇİZİLMİYOR
 *
 * `NegotiationItemView.targetChanged` sunucudan geliyor ve API'de kalıyor — semantiği
 * değişmedi. Değişen tek şey nerede GÖSTERİLDİĞİ.
 *
 * Ölçüldü: sunucu bu bayrağı `contract_contents.document` üzerinden türetiyor, yani
 * sözleşmenin ÇALIŞMA TASLAĞINDAN. Katılımcının önerisi ise paylaşılan bir SÜRÜME
 * dayanıyor (`sourceShareId` → `contract_versions.document`). Taslak ile yayımlanmış
 * sürüm normalde zaten farklı olduğu için bayrak öneri gönderilir gönderilmez yanıyordu —
 * kimse hiçbir şeyi değiştirmemişken.
 *
 * ⚠ Bayrak YANLIŞ DEĞİL, YANLIŞ YERDE: *"senin önerinden sonra hedef metin değişti"* diye
 * okunuyor ama *"önerdiğin sürüm çalışma taslağından farklı"* diyor. Katılımcı o taslağı
 * GÖREMİYOR; göremediği bir belgeye göre uyarı almak bilgi değil gürültü.
 *
 * Bayrağın tüketicisi KARAR VEREN taraftır ve taslağı yalnız sahip görüyor; bu yüzden
 * `ContractNegotiationSection`'da çiziliyor, burada çizilmiyor. Yokluğu ayrıca test
 * ediliyor — silmek yetmez, geri gelmemesi de ölçülmeli.
 *
 * ### ⚠ DÜĞMELER `accessLevel`'DAN TÜREMİYOR
 *
 * Sahibin paylaşım beyanı ile fiilî yapabilirlik AYRI eksenler (ölçüldü: katılım
 * satırının `accessLevel`/`permissions` alanları yazma yollarında hiç okunmuyor). Düğme
 * görünürlüğü gerçek kaynaklardan türüyor: kendi alanındaki rol (`can`), sunucunun
 * söylediği karar yönü (`decidingTenantId`) ve üzerinde çalışılacak malzemenin açık
 * paylaşımı. Beyandan türetseydik "Görüntüleme yazıyor ama karşı öneri düğmesi var"
 * çelişkisi doğardı.
 */
/**
 * ÖNERİNİN TABAN LİSTESİ — güncel sürüm ÖNCE (CLOS-1188).
 *
 * ### ⚠ ESKİ KURAL VAR OLAMAYAN BİR ÖZNEYİ KORUYORDU
 *
 * Eskiden "paylaşım varsa O kazanır" deniyordu ve gerekçesi *"sahip ona sözleşmenin
 * bütününü açmadı, tek tek nesne paylaştı"* idi. Bu önerme yanlış ve yanlış olmak zorunda:
 * nesne paylaşımının hedefi yalnız ORGANİZASYON olabiliyor ve hedef sözleşmenin AKTİF
 * katılımcısı olmak zorunda (aksi hâlde 422); `guest` ise katılım satırı olmadığı için bu
 * ekrana hiç gelmiyor. Yani nesne paylaşımı alan her özne, tanımı gereği sözleşmenin
 * bütününü gören taraftır.
 *
 * ⚠ Terminoloji tuzağı: eski yorum "haricî katılımcı" derken "misafir"i kastediyordu; oysa
 * depoda "haricî katılımcı" tam olarak katılım satırı olan taraf. İki kavram aynı kelimeyle
 * anılınca kural yanlış özneye bağlanmıştı.
 *
 * ### ⚠ ÖLÇÜLEN SONUÇ
 *
 * Karşı taraf, ekranda "mevcut" diye gösterilen ESKİ sürümün metnini düzenleyip öneri
 * gönderiyor; sahip o değişikliği zaten yapmış oluyor. Ürün öneriyi kaydeder kaydetmez
 * "eski bir sürüme dayanıyor" diye işaretliyordu — yani bayatlığı BİLİYOR, ama kullanıcıyı
 * ona doğru sürükledikten sonra söylüyordu.
 *
 * ### ⚠ PAYLAŞILAN SÜRÜMLER LİSTEDEN ATILMIYOR
 *
 * Sahip birden çok sürüm paylaşmış olabilir ve taban SEÇİMİ hâlâ anlamlı. Değişen yalnız
 * ÖNCELİK: güncel sürüm listeye giriyor ve varsayılan o oluyor (`versiyonlar[0]`).
 *
 * ⚠ Aynı sürüm iki kez GİRMİYOR: güncel sürüm zaten paylaşılmışsa listede tek satır kalıyor,
 * yoksa seçicide aynı numara iki kez görünürdü.
 */
export function oneriTabanlariniKur(
  guncel: PaylasilanVersiyon | null,
  paylasilanlar: PaylasilanVersiyon[],
): PaylasilanVersiyon[] {
  if (!guncel) return paylasilanlar;
  return [guncel, ...paylasilanlar.filter((v) => v.versionId !== guncel.versionId)];
}

export function SharedNegotiationSection({
  contractId,
  paylasilanVersiyonlar,
  kapsam,
}: {
  contractId: string;
  /** Öneri, üzerine kurulacak AÇIK bir versiyon paylaşımı olmadan gönderilemez. */
  paylasilanVersiyonlar: PaylasilanVersiyon[];
  /**
   * BU SÖZLEŞMEDEKİ KENDİ KATILIM KAPSAMIMIZ (Mega E2E BUG-008).
   *
   * ⚠ `can(...)` TEK BAŞINA YETMİYOR: o, kendi organizasyonumuzdaki rolümüzü söyler.
   * Sunucu ikinci bir eksen daha soruyor — bu sözleşmedeki katılım kapsamı
   * (`requireParticipantCapability`). İki eksen ayrıştığında ekran basılabilen ama 403
   * dönen düğmeler çiziyordu; canlı ölçümde "Kabul et"e basan karşı taraf tam olarak
   * bunu yaşadı ve müzakere döngüsü kapanamadı.
   */
  kapsam: string[];
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * ⚠ İKİ EKSEN BİRDEN: kendi rolüm VE bu sözleşmedeki katılım kapsamım. Sunucunun
   * sorduğu iki soruyu ekran da sormalı; biri eksik kalırsa kullanıcıya yapamayacağı bir
   * şey teklif edilir.
   */
  const yetkim = (anahtar: string) => can(anahtar) && kapsam.includes(anahtar);

  const anahtar = tenantKey(tenantId, "paylasilan-sozlesme", contractId, "muzakereler");
  const sorgu = useQuery({
    queryKey: anahtar,
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<NegotiationView[]>(`/api/shared-contracts/${contractId}/negotiations`, {
          tenantId,
        })
      ).data,
  });

  /** Sunucunun söylediği engel; başarılı bir denemede ve yeni denemede sıfırlanıyor. */
  const [oneriEngeli, setOneriEngeli] = useState<string | null>(null);

  const komut = useMutation({
    mutationFn: async (girdi: {
      negId: string;
      eylem: "withdraw" | "decide";
      itemId?: string;
      decision?: "accept" | "reject";
    }) =>
      api(
        girdi.eylem === "withdraw"
          ? `/api/shared-contracts/${contractId}/negotiations/${girdi.negId}/withdraw`
          : `/api/shared-contracts/${contractId}/negotiations/${girdi.negId}/items/${girdi.itemId}/decide`,
        {
          method: "POST",
          tenantId,
          ...(girdi.eylem === "decide"
            ? { body: JSON.stringify({ decision: girdi.decision }) }
            : {}),
        },
      ),
    onSuccess: async (_veri, girdi) => {
      toast.success(girdi.eylem === "withdraw" ? "Öneri geri çekildi" : "Karar kaydedildi");
      await queryClient.invalidateQueries({ queryKey: anahtar });
    },
    onError: (hata) => toast.error(resolveErrorState(hata, { idempotent: false }).title),
  });

  const oneri = useMutation({
    mutationFn: async (girdi: {
      negId?: string;
      baseVersionId: string;
      /** ⚠ Müvekkil Alanı yolunda YOK: orada nesne paylaşımı hiç kurulmuyor. */
      sourceShareId?: string;
      item: { targetClauseId: string; currentText: string; proposedText: string };
    }) =>
      api(
        girdi.negId
          ? `/api/shared-contracts/${contractId}/negotiations/${girdi.negId}/counter`
          : `/api/shared-contracts/${contractId}/negotiations`,
        {
          method: "POST",
          tenantId,
          headers: { "content-type": "application/json" },
          /**
           * ⚠ KARŞI ÖNERİDE `sourceShareId` GÖNDERİLMİYOR: sunucu onu parent setten
           * devralıyor (`counterNegotiationSchema` o alanı kabul etmiyor ve bilinmeyen
           * alan reddediliyor). Aynı gövdeyi iki uca yollamak 422 üretirdi.
           */
          body: JSON.stringify(
            girdi.negId
              ? { items: [girdi.item] }
              : {
                  baseVersionId: girdi.baseVersionId,
                  /**
                   * ⚠ ALAN YOKSA HİÇ GÖNDERİLMİYOR (`undefined` bile değil): sunucu
                   * şeması bilinmeyen/boş alanı reddediyor ve Müvekkil Alanı yolunda
                   * paylaşım kimliği diye bir şey YOK. Boş dize yollamak 422 üretirdi.
                   */
                  ...(girdi.sourceShareId ? { sourceShareId: girdi.sourceShareId } : {}),
                  items: [girdi.item],
                },
          ),
        },
      ),
    onMutate: () => setOneriEngeli(null),
    onSuccess: async (_veri, girdi) => {
      toast.success(girdi.negId ? "Karşı öneri gönderildi" : "Öneri gönderildi");
      await queryClient.invalidateQueries({ queryKey: anahtar });
    },
    /**
     * ⚠ 409 GEÇİCİ BİR BİLDİRİM DEĞİL — EKRANDA KALAN BİR DURUM.
     *
     * Sunucu "bu sözleşmede kurumunuza bağlı bir hukuki taraf yok" dediğinde kullanıcı
     * yeniden denemekle ilerleyemez; birinin sözleşmenin temsil edilen tarafını seçmesi
     * gerekiyor. Toast birkaç saniyede kaybolur ve geriye doldurulamayan bir form kalırdı.
     * Metin SUNUCUDAN geliyor: ekran engelin sebebini kendi tahmin etmiyor.
     */
    onError: (hata) => {
      if (hata instanceof ApiRequestError && hata.status === 409) {
        setOneriEngeli(hata.message);
        return;
      }
      toast.error(resolveErrorState(hata, { idempotent: false }).description);
    },
  });

  const setler = sorgu.data ?? [];

  /**
   * ÖNERİNİN TABANI — iki kaynak, tek liste (Client Area §6 · CLOS-1188).
   *
   * Kural `oneriTabanlariniKur` içinde ve saf; bileşene gömülü olduğu sürece
   * ölçülemiyordu.
   */
  const belge = useSharedCurrentVersion(contractId);
  const musteriAlaniTabani = useMemo<PaylasilanVersiyon | null>(() => {
    const surum = belge.data;
    if (!surum || surum.extraction.status !== "extracted") return null;
    const maddeler = surum.extraction.document?.clauses ?? [];
    /** ⚠ Madde yoksa form doldurulamaz; düğmeyi çizmek boş bir seçim kutusu demekti. */
    if (maddeler.length === 0) return null;
    return {
      shareId: null,
      versionId: surum.versionId,
      versionNumber: surum.versionNumber,
      clauses: maddeler,
    };
  }, [belge.data]);

  const oneriTabanlari = oneriTabanlariniKur(musteriAlaniTabani, paylasilanVersiyonlar);
  const oneriTabaniVar = oneriTabanlari.length > 0;

  return (
    <Card>
      <h2 className="mb-3 text-base font-semibold text-fg-heading">Değişiklik önerileri</h2>

      {sorgu.isPending ? (
        <div role="status" aria-label="Öneriler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : sorgu.error ? (
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              error: { title: "Öneriler yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : setler.length === 0 ? (
        <p data-testid="oneri-yok" className="text-sm text-fg-muted">
          Bu sözleşme üzerinde henüz bir değişiklik önerisi yok.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {setler.map((set) => {
            /** ⚠ Karar YÖNÜ sunucudan: kendi önerini karara bağlayamazsın. */
            const kararBizde = set.decidingTenantId === tenantId && set.status === "open";
            /** ⚠ Geri çekme ÖNERENİN hakkı — karar bekleyenin değil. */
            const bizimOneri = set.proposerTenantId === tenantId && set.status === "open";

            return (
              <div
                key={set.id}
                data-testid="muzakere-seti"
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={set.status === "open" ? "warning" : "neutral"}>
                    {NEGOTIATION_STATUS_NAMES[set.status]}
                  </Badge>
                  {/* Kim, kimin adına (CLOS-1152) — sahip ekranıyla aynı cümle. */}
                  <span className="text-xs text-fg-soft">
                    {attributionLabel({
                      actorName: set.proposerName,
                      onBehalfOf: set.actingPartyName,
                    })}
                  </span>
                  {/* ⚠ Bayat baz TÜRETİLMİŞ: öneri sözleşmenin güncel sürümüne değil. */}
                  {set.staleBase ? (
                    <Badge tone="warning">Öneri eski bir sürüme dayanıyor</Badge>
                  ) : null}
                </div>

                {/*
                  ⚠ ZİNCİR BAĞI (CLOS-252 Tren-2C): karşı öneri, cevapladığı öneriyle yan
                  yana duran KARDEŞ bir kart gibi okunuyordu. Kural sahip yüzeyiyle AYNI
                  kaynaktan (`findParentNegotiation`) geliyor.
                */}
                {(() => {
                  const parent = findParentNegotiation(setler, set);
                  return parent ? (
                    <p className="text-xs text-fg-soft">
                      ↳ {parent.proposerName} önerisine karşılık
                    </p>
                  ) : null;
                })()}

                {/* ⚠ Sonuçlanmış set kapanmış OKUNUYOR: sebep açıkça yazılıyor. */}
                {set.status === "countered" ? (
                  <p className="text-xs text-fg-soft">Bu öneri karşı öneriyle kapandı</p>
                ) : null}
                {set.status === "withdrawn" ? (
                  <p className="text-xs text-fg-soft">Öneren taraf bu öneriyi geri çekti</p>
                ) : null}

                {set.note ? <p className="text-sm text-fg">{set.note}</p> : null}

                <ul className="flex flex-col gap-2">
                  {set.items.map((oge) => (
                    <li key={oge.id} className="flex flex-col gap-1 rounded-md bg-muted p-2">
                      {/*
                        ⚠ MADDE ADI PAYLAŞILAN SÜRÜMDEN ÇÖZÜLÜYOR (CLOS-873 dönüşü).
                        Önceden ham UUID basılıyordu; kullanıcı hangi madde olduğunu
                        okuyamıyordu. `NegotiationItemView` başlık taşımıyor ve sunucuya
                        alan eklemek API yüzeyini büyütürdü — ekran zaten paylaşılan
                        sürümün maddelerini elinde tutuyor, adı oradan çözüyor.

                        ⚠ ÇÖZÜLEMEYEN KİMLİK SESSİZCE UUID'YE DÜŞMÜYOR: madde bize
                        paylaşılan hiçbir sürümde yoksa (kapsam dışı ya da sonradan
                        kaldırılmış) bunu AÇIKÇA söylüyoruz. "Ölçemedim" ile "temiz" ayrı
                        sonuçlardır; ham bir kimlik basmak ikisini birbirine karıştırırdı.

                        ⚠ `targetChanged` BU YÜZEYDE ÇİZİLMİYOR — gerekçe dosya
                        başlığında ("bayrak yanlış değil, yanlış yerde").
                      */}
                      <span className="text-xs text-fg-soft">
                        {/*
                          ⚠ AD ÇÖZÜMÜ ÖNERİNİN TABANIYLA AYNI KÜMEDEN (canlı ortamda ölçüldü).
                          Eskiden yalnız açık nesne paylaşımlarına bakılıyordu; Müvekkil
                          Alanı yolunda öyle bir paylaşım hiç yok, dolayısıyla temsilci
                          kendi gönderdiği öneride "Madde paylaşım kapsamınız dışında"
                          yazısını görüyordu — hem yanlış hem de kaygı verici bir cümle.
                        */}
                        {maddeAdi(oge.targetClauseId, oneriTabanlari)}
                      </span>
                      {/*
                        ⚠ ÜSTÜ ÇİZİLİ METİN CEVAPLANAN ŞEY (CLOS-252 Tren-2C): eskiden baz
                        versiyon metniydi ve zincirin ortasındaki halka kayboluyordu.
                        Etiket de türetmeden geliyor — parent bulunamazsa "Mevcut metin".
                      */}
                      {(() => {
                        const taban = answeredText(setler, set, oge);
                        return (
                          <>
                            <span className="text-xs text-fg-soft">
                              {taban.kind === "parent" ? "Cevaplanan öneri" : "Mevcut metin"}
                            </span>
                            <span className="text-xs text-fg-muted line-through">{taban.text}</span>
                          </>
                        );
                      })()}
                      <span className="text-sm text-fg">{oge.proposedText}</span>

                      <div className="flex flex-wrap items-center gap-2">
                        {/* ⚠ Rozet SET durumundan türüyor: kapanmış sette "Bekliyor" yok. */}
                        <Badge tone={oge.decision === "accepted" ? "success" : "neutral"}>
                          {negotiationItemStatusName(set.status, oge.decision)}
                        </Badge>
                        {kararBizde && oge.decision === "pending" && yetkim("contract.review") ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              data-testid="oge-kabul"
                              onClick={() =>
                                komut.mutate({
                                  negId: set.id,
                                  eylem: "decide",
                                  itemId: oge.id,
                                  decision: "accept",
                                })
                              }
                            >
                              Kabul et
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              data-testid="oge-ret"
                              onClick={() =>
                                komut.mutate({
                                  negId: set.id,
                                  eylem: "decide",
                                  itemId: oge.id,
                                  decision: "reject",
                                })
                              }
                            >
                              Reddet
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>

                {kararBizde && yetkim("contract.revision") && oneriTabaniVar ? (
                  <OneriFormu
                    etiket="Karşı öneri gönder"
                    testId={`karsi-oneri-${set.id}`}
                    versiyonlar={oneriTabanlari}
                    sabitVersiyonId={set.baseVersionId}
                    bekliyor={oneri.isPending}
                    onGonder={(item, versiyon) =>
                      oneri.mutate({
                        negId: set.id,
                        baseVersionId: versiyon.versionId,
                        /** ⚠ Karşı öneride sunucu zaten üst setten devralıyor. */
                        sourceShareId: versiyon.shareId ?? undefined,
                        item,
                      })
                    }
                  />
                ) : null}

                {bizimOneri && yetkim("contract.revision") ? (
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      data-testid="set-geri-cek"
                      loading={komut.isPending}
                      onClick={() => komut.mutate({ negId: set.id, eylem: "withdraw" })}
                    >
                      Öneriyi geri çek
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/*
        ⚠ ÖNERİ İÇİN ÜZERİNE ÇALIŞILACAK BİR METİN ŞART — ama "metin" iki yoldan gelebiliyor
        (`oneriTabanlari` gerekçesi). Taban yoksa düğmeyi çizmek kullanıcıyı doldurulamayan
        bir forma sokardı; sebebi söylemek, sessizce gizlemekten iyi.
      */}
      {yetkim("contract.revision") ? (
        oneriTabaniVar ? (
          <div className="mt-4 border-t border-border-default pt-4">
            <OneriFormu
              etiket="Öneri gönder"
              testId="yeni-oneri"
              versiyonlar={oneriTabanlari}
              bekliyor={oneri.isPending}
              onGonder={(item, versiyon) =>
                oneri.mutate({
                  baseVersionId: versiyon.versionId,
                  sourceShareId: versiyon.shareId ?? undefined,
                  item,
                })
              }
            />
            {/*
              ⚠ Engel FORMUN YANINDA duruyor, sayfanın tepesinde değil: kullanıcı az önce
              doldurduğu alanla neden ilerleyemediğini aynı yerde okuyor.
            */}
            {oneriEngeli ? (
              <p data-testid="oneri-engeli" role="status" className="mt-3 text-xs text-danger">
                {oneriEngeli}
              </p>
            ) : null}
          </div>
        ) : (
          <p data-testid="oneri-kapali" className="mt-3 text-xs text-fg-soft">
            Öneri gönderebilmeniz için üzerinde çalışılabilecek bir sözleşme metni gerekiyor.
          </p>
        )
      ) : null}
    </Card>
  );
}

/**
 * Karşı tarafa AÇIKÇA paylaşılmış bir sözleşme versiyonu ve içindeki maddeler.
 *
 * ⚠ `shareId` taşınıyor çünkü sunucu `sourceShareId` istiyor ve paylaşımın AKTİF, hedefi
 * biz, nesnesi taban versiyon olduğunu doğruluyor. Versiyon kimliğini paylaşımdan
 * ayrıştırıp yollasaydık, bize paylaşılmamış bir versiyona öneri açmayı deneyip 422
 * alırdık.
 */
export type PaylasilanVersiyon = {
  /** ⚠ Müvekkil Alanı tabanında YOK: orada nesne paylaşımı hiç kurulmuyor. */
  shareId: string | null;
  versionId: string;
  versionNumber: number | null;
  clauses: ContractClause[];
};

/**
 * Detay yükündeki açık versiyon paylaşımlarını öneri formunun girdisine çevirir.
 *
 * ⚠ İÇERİK BEYAZ-LİSTELİ ADAPTÖRDEN geliyor (`objectShares.ts`): versiyon paylaşımı
 * `document` alanını taşıyor, yani maddeler alıcının elinde. Bu yüzden öneri formu
 * sözleşmenin tamamını istemeden çalışabiliyor — zaten isteyemezdi de, paylaşılmayan
 * hiçbir şey bu ekrana gelmiyor.
 *
 * ⚠ Yapı DEFANSİF okunuyor: `content` sunucudan gelen serbest bir kayıt ve şekli
 * adaptörün beyaz listesine bağlı. Şekil beklenmedikse madde listesi BOŞ kalıyor —
 * uydurma bir madde göstermek, kullanıcıya var olmayan bir metni önerttirirdi.
 */
export function paylasilanVersiyonlar(nesneler: readonly SharedObjectView[]): PaylasilanVersiyon[] {
  return nesneler
    .filter((n) => n.objectType === "version")
    .map((n) => {
      const icerik = n.content as { versionNumber?: unknown; document?: unknown };
      const belge = icerik.document as { clauses?: unknown } | null | undefined;
      const maddeler = Array.isArray(belge?.clauses) ? (belge.clauses as ContractClause[]) : [];
      return {
        shareId: n.shareId,
        versionId: n.objectId,
        versionNumber: typeof icerik.versionNumber === "number" ? icerik.versionNumber : null,
        clauses: maddeler,
      };
    });
}

/**
 * Bir maddenin EKRAN ADI — paylaşılan sürümlerden çözülüyor.
 *
 * ⚠ Çözülemeyen kimlik ham UUID'ye DÜŞMÜYOR. Madde bize paylaşılan hiçbir sürümde yoksa
 * — paylaşım kapsamı dışında ya da sonradan kaldırılmış — kullanıcıya durumu söylüyoruz.
 * Ham kimlik basmak "bilmiyorum" ile "şu madde" arasındaki farkı silerdi.
 *
 * ⚠ Başlığı BOŞ olan madde de gerçek bir maddedir (`contractClauseSchema` boş başlığa
 * izin veriyor: numarasız serbest bölüm). Onu "çözülemedi" saymak yanlış olurdu — ayrı
 * bir metinle anılıyor.
 */
export function maddeAdi(clauseId: string, versiyonlar: readonly PaylasilanVersiyon[]): string {
  for (const versiyon of versiyonlar) {
    const madde = versiyon.clauses.find((c) => c.id === clauseId);
    if (madde) return madde.title.length > 0 ? `Madde: ${madde.title}` : "Başlıksız madde";
  }
  return "Madde paylaşım kapsamınız dışında";
}

/** Bir maddenin düz metni — bloklar birleştirilerek. */
function maddeMetni(madde: ContractClause): string {
  return madde.blocks.map((b) => b.text).join("\n");
}

/**
 * ÖNERİ GÖNDERME YÜZEYİ (CLOS-232 değil — CLOS-873 dilim-1'in eksik kalan yarısı).
 *
 * ### ⚠ BU YÜZEY BİR BOŞLUĞUN KAPATILMASI
 *
 * İlk teslimde yalnız uyarı vardı: *"öneri gönderebilmeniz için sahibin size bir versiyon
 * paylaşması gerekiyor."* Paylaşım geldiğinde uyarı kayboluyor ve YERİNE HİÇBİR ŞEY
 * gelmiyordu — yani kullanıcı koşulu sağladığında da öneri gönderemiyordu. Testi de
 * yalnız uyarının YOKLUĞUNU ölçüyordu; yokluk iddiası, varlığı garanti etmiyor.
 *
 * ### ⚠ TEK MADDE, TEK ÖĞE — bilinçli sadelik
 *
 * Sunucu bir sette 50 öğeye kadar kabul ediyor. Bu yüzey bir seferde BİR madde öneriyor:
 * çok öğeli bir düzenleyici kendi kartını hak eden ayrı bir iş (sıralama, kısmi silme,
 * taslak). Sınır burada yazılı ki "eksik" değil "dar" olduğu görünsün.
 *
 * ### ⚠ MEVCUT METİN KULLANICIDAN DEĞİL BELGEDEN
 *
 * `currentText` seçilen maddenin paylaşılan versiyondaki metni. Kullanıcıya yazdırsaydık
 * sunucunun "hedef metin bu öneriden sonra değişti" türetmesi anlamsızlaşırdı — o
 * karşılaştırmanın bir tarafı uydurma olurdu.
 */
function OneriFormu({
  etiket,
  testId,
  versiyonlar,
  sabitVersiyonId,
  bekliyor,
  onGonder,
}: {
  etiket: string;
  testId: string;
  versiyonlar: PaylasilanVersiyon[];
  /** Karşı öneride taban versiyon SETTEN gelir; kullanıcı seçmiyor. */
  sabitVersiyonId?: string;
  bekliyor: boolean;
  onGonder: (
    item: { targetClauseId: string; currentText: string; proposedText: string },
    versiyon: PaylasilanVersiyon,
  ) => void;
}) {
  const [acik, setAcik] = useState(false);
  const [versiyonId, setVersiyonId] = useState(sabitVersiyonId ?? versiyonlar[0]?.versionId ?? "");
  const [maddeId, setMaddeId] = useState("");
  const [metin, setMetin] = useState("");

  const versiyon = versiyonlar.find((v) => v.versionId === versiyonId) ?? versiyonlar[0];
  const madde = versiyon?.clauses.find((c) => c.id === maddeId);
  const mevcut = madde ? maddeMetni(madde) : "";

  if (!acik) {
    return (
      <Button type="button" size="sm" data-testid={`${testId}-ac`} onClick={() => setAcik(true)}>
        {etiket}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {/*
        ⚠ Taban versiyon KARŞI ÖNERİDE seçilemiyor: set zaten bir tabana dayanıyor ve
        başka bir tabana karşı öneri vermek zinciri kopanırdı.
      */}
      {sabitVersiyonId === undefined && versiyonlar.length > 1 ? (
        <FormField label="Paylaşılan versiyon">
          <Select
            value={versiyonId}
            data-testid={`${testId}-versiyon`}
            onChange={(o) => {
              setVersiyonId(o.target.value);
              setMaddeId("");
            }}
          >
            {/* ⚠ Anahtar SÜRÜMDEN: Müvekkil Alanı tabanında paylaşım kimliği yok. */}
            {versiyonlar.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                {v.versionNumber === null ? "Paylaşılan versiyon" : `Sürüm ${v.versionNumber}`}
              </option>
            ))}
          </Select>
        </FormField>
      ) : null}

      <FormField label="Madde">
        <Select
          value={maddeId}
          data-testid={`${testId}-madde`}
          onChange={(o) => {
            setMaddeId(o.target.value);
            const secilen = versiyon?.clauses.find((c) => c.id === o.target.value);
            /** Önerilen metin MEVCUT metinden başlıyor: kullanıcı sıfırdan yazmıyor, düzenliyor. */
            setMetin(secilen ? maddeMetni(secilen) : "");
          }}
        >
          <option value="">Seçiniz…</option>
          {(versiyon?.clauses ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.title.length > 0 ? c.title : "Başlıksız madde"}
            </option>
          ))}
        </Select>
      </FormField>

      {madde ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-fg-soft">Mevcut metin</span>
          <p
            data-testid={`${testId}-mevcut`}
            className="rounded-md bg-muted p-2 text-xs text-fg-muted"
          >
            {mevcut}
          </p>
        </div>
      ) : null}

      <FormField label="Önerilen metin">
        <Textarea
          value={metin}
          rows={4}
          data-testid={`${testId}-metin`}
          onChange={(o) => setMetin(o.target.value)}
        />
      </FormField>

      <div className="flex flex-wrap gap-2">
        {/*
          ⚠ DÜĞME ÜÇ KOŞULA BAĞLI ve üçü de gerçek: madde seçili, metin boş değil, metin
          MEVCUTTAN FARKLI. Aynı metni öneren bir set sunucuda geçerli olurdu ama karşı
          tarafa karar verilecek hiçbir şey taşımazdı.
        */}
        <Button
          type="button"
          size="sm"
          data-testid={`${testId}-gonder`}
          loading={bekliyor}
          disabled={madde === undefined || metin.trim().length === 0 || metin === mevcut}
          onClick={() => {
            if (!madde || !versiyon) return;
            onGonder(
              { targetClauseId: madde.id, currentText: mevcut, proposedText: metin },
              versiyon,
            );
            setAcik(false);
            setMaddeId("");
            setMetin("");
          }}
        >
          Gönder
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setAcik(false)}>
          Vazgeç
        </Button>
      </div>
    </div>
  );
}
